<template>
    <li
        v-if="chat"
        :class="!activeStatusFilter && chat.lastConversation.status === 'closed' ? 'opacity-50' : ''"
        class="pb-0"
        v-on="$listeners"
    >
        <b-avatar
            v-b-tooltip.hover.right="
                (chat.contact && chat.contact.isHighPriority === 1) ||
                getChannelSettingValue(chat.channel, 'priority') === '1'
                    ? $t('important-contact-or-channel')
                    : ''
            "
            :badge="
                (chat.contact && chat.contact.isHighPriority === 1) ||
                getChannelSettingValue(chat.channel, 'priority') === '1'
            "
            :badge-variant="resolvePriorityVariant(chat)"
            :text="resolveInitials(chat.contact && chat.contact.name)"
            :variant="multiSelectEnabled ? '' : 'tertiary'"
            badge-left
            badge-offset="-6px"
            badge-top
            class="round-large badge-vip mr-50 multi-select-checkbox"
            size="34"
        >
            <template #badge>
                <font-awesome-icon icon="crown" />
            </template>

            <template v-if="multiSelectEnabled" #default>
                <b-form-checkbox :checked="findChatInMultiSelect && findChatInMultiSelect.id" :value="chat.id" />
            </template>
        </b-avatar>

        <div class="chat-info flex-grow-1">
            <div class="d-flex flex-column flex-grow-1 justify-content-between h-100">
                <div class="d-flex justify-content-between align-items-start">
                    <!-- Contact & last message -->
                    <div class="overflow-hidden">
                        <h5 :class="{ 'font-weight-extra-bold': !chat.read }" class="text-truncate m-0 contact-name">
                            <font-awesome-icon
                                v-if="chat.contact && chat.contact.isBlocked"
                                class="text-dark mr-25"
                                icon="ban"
                            />
                            {{ (chat.contact && chat.contact.name) || `(${$t("entities.contact.contact-unknown")})` }}
                            <font-awesome-icon
                                v-if="chat.storedMessage"
                                v-b-tooltip.hover.bottom="`${$t('Draft')}: ${formatStrForTooltips(chat.storedMessage)}`"
                                class="text-secondary"
                                icon="pen"
                                size="sm"
                            />
                        </h5>

                        <template>
                            <template
                                v-if="
                                    inboxModuleType === InboxModuleType.MESSAGING &&
                                    chatTypingIndicators &&
                                    chatTypingIndicators.count > 0
                                "
                            >
                                <p class="text-secondary text-truncate mb-1">
                                    <font-awesome-icon icon="comment-dots" />
                                    {{ chatTypingIndicators.names }}
                                    {{ chatTypingIndicators.count === 1 ? $t("is") : $t("are") }}
                                    {{ $t("typing") }}
                                </p>
                            </template>
                            <template v-else-if="chat.lastConversation.lastEntry">
                                <p
                                    :class="{ 'font-weight-extra-bold text-dark': !chat.read }"
                                    :title="
                                        chat.channel && chat.channel.platform !== 'EMAIL'
                                            ? formatStrForTooltips(chat.lastConversation.lastEntry.message)
                                            : ''
                                    "
                                    class="text-secondary text-truncate pb-1 m-0"
                                >
                                    <font-awesome-icon
                                        v-if="resolveEntryIcon(chat.lastConversation.lastEntry)"
                                        :icon="resolveEntryIcon(chat.lastConversation.lastEntry)"
                                        class="mr-25"
                                    />
                                    {{ resolveLastEntryMessage(chat.lastConversation.lastEntry, chat.platform) }}
                                </p>
                            </template>
                            <template v-else>
                                <p
                                    :class="{ 'font-weight-extra-bold text-dark': !chat.read }"
                                    class="text-secondary text-truncate mb-1"
                                >
                                    {{ $t("inbox.no-messages") }}
                                </p>
                            </template>
                        </template>
                    </div>

                    <!-- Time / Customer Care Window -->
                    <div class="chat-meta d-flex">
                        <span
                            v-if="chat.lastConversation.lastEntry"
                            :class="!chat.read ? 'text-primary font-weight-bold' : 'text-chat-time'"
                            class="chat-time mb-25"
                        >
                            {{ timeAgo(chat.lastConversation.lastEntry.time) }}
                        </span>

                        <b-badge
                            v-if="chat.unreadMessages > 0 && !chat.read"
                            class="d-flex justify-content-center align-items-center p-0 ml-25"
                            pill
                            variant="messenger"
                        >
                            <b>{{ chat.unreadMessages < 100 ? chat.unreadMessages : "99+" }}</b>
                        </b-badge>

                        <b-badge
                            v-if="!resolveCustomerCareWindow"
                            v-b-tooltip.hover.bottom="$t('customer-care-window-closed')"
                            class="d-flex justify-content-center align-items-center p-0 ml-25"
                            pill
                            variant="warning"
                        >
                            <font-awesome-icon icon="bell" />
                        </b-badge>
                    </div>
                </div>

                <!-- Assignee & Platform -->
                <div class="d-flex justify-content-between align-items-center flex-nowrap">
                    <div class="assignee-container">
                        <b-badge
                            v-if="chat.assignedTeam"
                            :style="{
                                'background-color': chat.assignedTeam.color,
                                color: textColorByBackgroundColor(chat.assignedTeam.color),
                            }"
                            class="mr-1 text-truncate"
                        >
                            <template v-if="chat.assignedUserId">
                                {{ chat.assignee ? chat.assignee.name : $t("entities.user.user-not-found") }} -
                            </template>

                            {{ chat.assignedTeam.name }}

                            <span
                                v-if="chat.assignee"
                                :class="`badge-${resolveUserStatus(chat.assignee.status)}`"
                                class="status-indicator round"
                            />
                        </b-badge>

                        <b-badge
                            v-else
                            :class="!chat.assignee ? 'unassigned' : 'text-white'"
                            :variant="chat.assignee ? 'primary' : ''"
                            class="font-weight-normal mr-1 text-truncate"
                            small
                        >
                            <!-- If assigned, but no assignee, then user was not found -->
                            <template v-if="chat.assignedUserId && !chat.assignee">
                                {{ $t("entities.user.user-not-found") }}
                            </template>
                            <template v-else>
                                {{ chat.assignee ? chat.assignee.name : $t("entities.conversation.Unassigned") }}
                            </template>

                            <span
                                v-if="chat.assignee"
                                :class="`badge-${resolveUserStatus(chat.assignee.status)}`"
                                class="status-indicator round"
                            />
                        </b-badge>
                    </div>
                    <div class="platform-icon">
                        <b-img
                            v-b-tooltip.hover.bottom="chat.channel && chat.channel.name"
                            :src="
                                chat.channel
                                    ? require(`@/assets/images/platforms/` +
                                          resolvePlatformIconSvgSrc(chat.channel.provider, chat.channel.platform) +
                                          `.svg`)
                                    : ''
                            "
                        />
                    </div>
                </div>
            </div>
        </div>
    </li>
</template>

<script>
import { computed } from "@vue/composition-api";
import { BAvatar, BAvatarGroup, BBadge, BCol, BFormCheckbox, BImg, BRow, VBTooltip } from "bootstrap-vue";
import { timeAgo } from "@/utils/luxon";
import { resolveEntryIcon, resolveRemainingCustomerCareWindow } from "@/utils/chats";
import { textColorByBackgroundColor } from "@/utils/color";
import { resolveInitials, resolveUserStatus } from "@/utils/users";
import {
    getChannelSettingValue,
    resolveLastEntryMessage,
    resolvePlatformIcon,
    resolvePlatformIconSvgSrc,
    resolvePriorityVariant,
    resolveProviderColor,
} from "@/utils/channels";
import useTypingIndicators from "@/views/modules/messaging-inbox/useTypingIndicators";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import { InboxModuleType } from "@/types/inboxModule.type";
import i18n from "@/libs/i18n";

export default {
    components: {
        BAvatar,
        BBadge,
        BAvatarGroup,
        BCol,
        BRow,
        BImg,
        BFormCheckbox,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        chat: {
            type: Object,
            required: true,
        },
        teams: {
            type: Array,
            required: true,
        },
        activeStatusFilter: {
            type: Object,
            required: false,
        },
        multiSelectEnabled: {
            type: Boolean,
            required: true,
        },
        multiSelectChats: {
            type: Array,
            required: false,
        },
    },
    setup(props) {
        const assigneeTeams = computed(() => {
            if (props.chat.assignee) {
                return props.teams.filter((team) => {
                    const teamUsers = team.teamUsers.filter((teamUser) => teamUser.userId === props.chat.assignee.id);

                    if (teamUsers.length > 0) {
                        return team;
                    }
                });
            }
        });

        // is used to kill the html inside the tooltips and avoid risk of html attack
        const formatStrForTooltips = (chatMessage) => {
            chatMessage = chatMessage.toString();
            chatMessage = chatMessage.replace(/(<([^>]+)>)/gi, " ");
            if (chatMessage.length > 100) {
                chatMessage = chatMessage.substring(0, 90) + i18n.t("click-to-see-more");
            }
            return chatMessage;
        };

        // @notice only messaging chats do have typing indicators, so actually this chat.vue shouldn't be a shared component,
        // maybe we can add typing indicators for email later instead of making this component non-shared.
        const { resolveTypingIndicatorsForChat } = useTypingIndicators();
        const chatTypingIndicators = computed(() => resolveTypingIndicatorsForChat(props.chat.id));

        const { inboxModuleType } = useInboxModuleType();

        return {
            inboxModuleType,
            InboxModuleType,
            chatTypingIndicators,
            resolveProviderColor,
            resolvePlatformIcon,
            timeAgo,
            getChannelSettingValue,
            resolvePriorityVariant,
            resolveEntryIcon,
            resolveLastEntryMessage,
            resolveInitials,
            resolveUserStatus,
            textColorByBackgroundColor,
            resolvePlatformIconSvgSrc,
            assigneeTeams,
            formatStrForTooltips,
            findChatInMultiSelect: computed(() =>
                props.multiSelectChats.find((selectedChat) => selectedChat.id === props.chat.id)
            ),
            resolveCustomerCareWindow: computed(() =>
                resolveRemainingCustomerCareWindow(
                    props.chat.lastInboundMessageTime,
                    props.chat.channel?.customerCareWindowLength
                )
            ),
        };
    },
};
</script>
